import { createApi, createCustomServiceCall } from '@thinknimble/tn-models'
import { axiosInstance } from '../axios-instance'
import { z } from 'zod'
import {
  orgWalletSummaryShape,
  rateCardShape,
  usageEventTypeEnum,
  usageGroupByEnum,
  usageRecordShape,
  walletShape,
} from './models'

/** List the caller's orgs with their wallet balances (for org pickers). */
const listWallets = createCustomServiceCall({
  outputShape: z.array(z.object(orgWalletSummaryShape)),
  cb: async ({ client, utils: { fromApi } }) => {
    const response = await client.get('/billing/wallets/')
    return fromApi(response.data)
  },
})

/** Fetch the org's wallet balance, currency, and recent top-ups. */
const getWallet = createCustomServiceCall({
  inputShape: { organization: z.string().uuid() },
  outputShape: walletShape,
  cb: async ({ client, input, utils: { fromApi } }) => {
    const response = await client.get('/billing/wallet/', {
      params: { organization: input.organization },
    })
    return fromApi(response.data)
  },
})

/** Fetch usage aggregated by period and event type over a date range. */
const getUsage = createCustomServiceCall({
  inputShape: {
    organization: z.string().uuid(),
    startDate: z.string().optional(),
    endDate: z.string().optional(),
    eventType: z.enum(usageEventTypeEnum).optional(),
    groupBy: z.enum(usageGroupByEnum).optional(),
  },
  outputShape: z.array(z.object(usageRecordShape)),
  cb: async ({ client, input, utils: { fromApi } }) => {
    const response = await client.get('/billing/usage/', {
      params: {
        organization: input.organization,
        start_date: input.startDate,
        end_date: input.endDate,
        event_type: input.eventType,
        group_by: input.groupBy,
      },
    })
    return fromApi(response.data)
  },
})

/** Fetch the active rate card for every billable event type.
 *
 * The pricing itself is global, but the endpoint gates on ``billing.manage`` in
 * the given org, so the caller must pass the organization for the permission
 * check.
 */
const getRates = createCustomServiceCall({
  inputShape: { organization: z.string().uuid() },
  outputShape: z.array(z.object(rateCardShape)),
  cb: async ({ client, input, utils: { fromApi } }) => {
    const response = await client.get('/billing/rates/', {
      params: { organization: input.organization },
    })
    return fromApi(response.data)
  },
})

/** Open a Stripe Checkout Session to top the wallet up; returns the hosted URL. */
const createTopUp = createCustomServiceCall({
  inputShape: { organization: z.string().uuid(), amount: z.string() },
  outputShape: { checkoutUrl: z.string() },
  cb: async ({ client, input, utils: { fromApi } }) => {
    const response = await client.post('/billing/topup/', {
      organization: input.organization,
      amount: input.amount,
    })
    return fromApi(response.data)
  },
})

// Billing exposes no REST entity of its own — every call is a custom read/action
// below. createApi still requires an entity shape, so give it a minimal stub
// with an id; the standard CRUD methods are never used.
const billingEntityShape = { id: z.string() }

export const billingApi = createApi({
  client: axiosInstance,
  baseUri: '/billing/',
  models: {
    entity: billingEntityShape,
  },
  customCalls: {
    listWallets,
    getWallet,
    getUsage,
    getRates,
    createTopUp,
  },
})
